import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { downloadExport } from '@/lib/exporter';

export const StockPage = () => {
  const { t } = useLanguage();
  const [warehouseStock, setWarehouseStock] = useState([]);
  const [pharmacyStock, setPharmacyStock] = useState([]);
  const [products, setProducts] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [filterPharmacy, setFilterPharmacy] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [newBatch, setNewBatch] = useState({ product_id: '', batch_number: '', expiry_month: 12, expiry_year: 2027, quantity_received: 100 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [wh, ph, prods, pharms] = await Promise.all([
        api.get('/stock-ledger/warehouse'),
        api.get('/stock-ledger'),
        api.get('/products'),
        api.get('/pharmacies')
      ]);
      setWarehouseStock(wh.data);
      setPharmacyStock(ph.data);
      setProducts(prods.data);
      setPharmacies(pharms.data);
    } catch (e) {
      console.error(e);
    }
  };

  const getExpiryColor = (m, y) => {
    const now = new Date();
    const expiry = new Date(y, m - 1);
    const monthsDiff = (expiry.getFullYear() - now.getFullYear()) * 12 + (expiry.getMonth() - now.getMonth());
    if (expiry < new Date(now.getFullYear(), now.getMonth(), 1)) return 'bg-red-100 text-red-800 border-red-300 font-bold';
    if (monthsDiff <= 3) return 'bg-red-50 text-red-700 border-red-200';
    if (monthsDiff <= 6) return 'bg-orange-50 text-orange-700 border-orange-200';
    if (monthsDiff <= 9) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    if (monthsDiff <= 12) return 'bg-blue-50 text-blue-700 border-blue-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  // A7: status uses the SAME expiry rules as the Action Center. Slow-moving is
  // tagged separately from expiry so a fresh batch with 0 sales isn't flagged
  // "Recall suggested" on expiry grounds.
  const getStockStatus = (item) => {
    const now = new Date();
    const expiry = new Date(item.expiry_year, item.expiry_month - 1);
    const monthsDiff = (expiry.getFullYear() - now.getFullYear()) * 12 + (expiry.getMonth() - now.getMonth());
    if (expiry < new Date(now.getFullYear(), now.getMonth(), 1)) {
      return { label: 'EXPIRED', cls: 'bg-red-600 text-white font-bold' };
    }
    if (monthsDiff <= 3) return { label: 'Recall suggested', cls: 'bg-red-100 text-red-700' };
    if (monthsDiff <= 6) return { label: 'Expiring ≤6 mo', cls: 'bg-orange-100 text-orange-700' };
    if (monthsDiff <= 9) return { label: 'Expiring ≤9 mo', cls: 'bg-yellow-100 text-yellow-700' };
    if (monthsDiff <= 12) return { label: 'Expiring ≤12 mo', cls: 'bg-blue-100 text-blue-700' };
    const sold = item.last_month_sold || 0;
    if (sold >= 10) return { label: 'Fast-moving', cls: 'bg-green-100 text-green-700' };
    if (sold >= 3) return { label: 'Normal', cls: 'bg-slate-100 text-slate-700' };
    if (item.quantity <= 5) return { label: 'Low stock', cls: 'bg-orange-100 text-orange-700' };
    return { label: 'Slow-moving', cls: 'bg-slate-100 text-slate-600' };
  };

  const getPharmacyName = (id) => pharmacies.find(p => p.id === id)?.name || 'Unknown';

  const handleCreateBatch = async () => {
    try {
      await api.post('/batches', newBatch);
      toast.success('Batch created successfully');
      setBatchDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to create batch');
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>
          {t('nav.stock')}
        </h1>
        <div className="flex gap-2">
          <Button data-testid="export-stock-btn" variant="outline" onClick={() => downloadExport('/exports/stock', 'Stock.xlsx')}>
            <DownloadSimple size={14} className="mr-2" /> Export
          </Button>
        <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-batch-btn" style={{ backgroundColor: '#1E3A8A' }}>
              <Plus size={16} className="mr-2" /> Intake New Batch
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Batch Intake</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Product</Label>
                <Select value={newBatch.product_id} onValueChange={(v) => setNewBatch({...newBatch, product_id: v})}>
                  <SelectTrigger data-testid="batch-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Batch Number</Label>
                <Input data-testid="batch-number-input" value={newBatch.batch_number} onChange={(e) => setNewBatch({...newBatch, batch_number: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Expiry Month</Label>
                  <Input type="number" min="1" max="12" value={newBatch.expiry_month} onChange={(e) => setNewBatch({...newBatch, expiry_month: parseInt(e.target.value)})} />
                </div>
                <div>
                  <Label>Expiry Year</Label>
                  <Input type="number" value={newBatch.expiry_year} onChange={(e) => setNewBatch({...newBatch, expiry_year: parseInt(e.target.value)})} />
                </div>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input type="number" value={newBatch.quantity_received} onChange={(e) => setNewBatch({...newBatch, quantity_received: parseInt(e.target.value)})} />
              </div>
              <Button data-testid="submit-batch-btn" onClick={handleCreateBatch} className="w-full" style={{ backgroundColor: '#1E3A8A' }}>Create Batch</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Tabs defaultValue="pharmacy">
        <TabsList>
          <TabsTrigger value="pharmacy" data-testid="tab-pharmacy-stock">Pharmacy Stock</TabsTrigger>
          <TabsTrigger value="warehouse" data-testid="tab-warehouse-stock">Warehouse Batches</TabsTrigger>
        </TabsList>

        <TabsContent value="pharmacy">
          <div className="flex gap-2 mb-3 flex-wrap">
            <select value={filterPharmacy} onChange={e => setFilterPharmacy(e.target.value)} className="text-sm border rounded px-2 py-1" data-testid="filter-pharmacy">
              <option value="">All pharmacies</option>
              {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={filterProduct} onChange={e => setFilterProduct(e.target.value)} className="text-sm border rounded px-2 py-1" data-testid="filter-product">
              <option value="">All products</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3">Pharmacy</th>
                  <th className="text-left p-3">Product</th>
                  <th className="text-left p-3">Batch</th>
                  <th className="text-left p-3">Expiry</th>
                  <th className="text-right p-3">Quantity</th>
                  <th className="text-right p-3">Last Mo. Sold</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {pharmacyStock
                  .filter(item => !filterPharmacy || item.pharmacy_id === filterPharmacy)
                  .filter(item => !filterProduct || item.product_id === filterProduct)
                  .map((item, idx) => {
                    const sold = item.last_month_sold || 0;
                    const status = getStockStatus(item);
                    return (
                      <tr key={idx} className="border-t border-slate-200">
                        <td className="p-3">{getPharmacyName(item.pharmacy_id)}</td>
                        <td className="p-3 font-medium">{item.product_name}</td>
                        <td className="p-3 text-slate-600 font-mono text-xs">{item.batch_number}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getExpiryColor(item.expiry_month, item.expiry_year)}`}>
                            {String(item.expiry_month).padStart(2, '0')}/{item.expiry_year}
                          </span>
                        </td>
                        <td className="p-3 text-right font-bold">{item.quantity} <span className="text-xs text-slate-400">{item.unit_label}</span></td>
                        <td className="p-3 text-right">{sold}</td>
                        <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${status.cls}`}>{status.label}</span></td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="warehouse">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3">Product</th>
                  <th className="text-left p-3">Batch</th>
                  <th className="text-left p-3">Expiry</th>
                  <th className="text-right p-3">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {warehouseStock.map((item, idx) => (
                  <tr key={idx} className="border-t border-slate-200">
                    <td className="p-3 font-medium">{item.product_name}</td>
                    <td className="p-3 text-slate-600">{item.batch_number}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getExpiryColor(item.expiry_month, item.expiry_year)}`}>
                        {String(item.expiry_month).padStart(2, '0')}/{item.expiry_year}
                      </span>
                    </td>
                    <td className="p-3 text-right font-bold">{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
